import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import { config, logger, endpoints } from '../config'
import { getAuthToken, clearAuth, getUserInfo, isTokenExpired, refreshToken } from './auth'
import { v4 as uuidv4 } from 'uuid';
// 基礎響應接口
export interface Response {
  success: boolean
  errCode: string
  errMsg: string
}

// 單條數據響應
export interface SingleResponse<T> extends Response {
  data: T
}

// 多條數據響應
export interface MultiResponse<T> extends Response {
  data: T[]
}

// 分頁數據響應
export interface PageResponse<T> extends Response {
  data: T[]
  total: number
}


// 請求配置類型
export interface RequestConfig extends AxiosRequestConfig {
  skipAuth?: boolean // 跳過認證
  skipErrorHandler?: boolean // 跳過統一錯誤處理
  showLoading?: boolean // 顯示載入狀態
  retryAttempts?: number // 重試次數
}

// 創建axios實例
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: config.api.baseURL,
    timeout: config.api.timeout,
    withCredentials: true, // 允許攜帶認證信息（cookies、authorization headers等）
    headers: {
      'Content-Type': 'application/json'
    }
  })

  // 請求攔截器
  instance.interceptors.request.use(
    async (config) => {
      // 初始化headers
      config.headers = config.headers || {}
      config.headers["X-Idempotent-Key"] = uuidv4()
      // 檢查是否需要認證
      const requestConfig = config as RequestConfig
      if (!requestConfig.skipAuth) {
        // 檢查token是否過期，需要刷新
        if (isTokenExpired()) {
          logger.warn('Token即將過期，嘗試刷新')
          try {
            await refreshToken()
          } catch (error) {
            logger.error('Token刷新失敗:', error)
            // 刷新失敗，跳轉到登錄頁面或清除認證
            clearAuth()
            throw new Error('Token刷新失敗，請重新登錄')
          }
        }
        
        // 添加認證token
        const token = getAuthToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
      }

      // 添加請求ID用於追踪
      config.headers['X-Request-ID'] = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      
      // 添加用戶信息
      const userInfo = getUserInfo()
      if (userInfo?.userId) {
        config.headers['X-User-ID'] = userInfo.userId
      }

      logger.debug('Request:', {
        url: config.url,
        method: config.method?.toUpperCase(),
        headers: config.headers,
        data: config.data
      })

      return config
    },
    (error: AxiosError) => {
      logger.error('Request interceptor error:', error)
      return Promise.reject(error)
    }
  )

  // 響應攔截器
  instance.interceptors.response.use(
    (response: AxiosResponse<Response>) => {
      const { data, config } = response

      logger.debug('Response:', {
        url: config.url,
        status: response.status,
        data
      })

      // 統一處理業務錯誤
      if (data && typeof data.success !== 'undefined' && !data.success) {
        const error = new Error(data.errMsg || '請求失敗') as Error & { code: string; response: AxiosResponse }
        error.code = data.errCode
        error.response = response
        
        // 如果不跳過錯誤處理
        if (!(config as RequestConfig).skipErrorHandler) {
          handleBusinessError(data)
        }
        
        return Promise.reject(error)
      }

      return response
    },
    (error: AxiosError) => {
      const { response, config } = error

      logger.error('Response error:', {
        url: config?.url,
        status: response?.status,
        message: error.message,
        data: response?.data
      })

      // HTTP錯誤處理
      if (response) {
        handleHttpError(response.status, response.data as Response, config as RequestConfig)
      } else if (error.code === 'ECONNABORTED') {
        showErrorMessage('請求超時，請檢查網絡連接')
      } else if (error.message.includes('Network Error')) {
        showErrorMessage('網絡連接失敗，請檢查網絡設置')
      }

      return Promise.reject(error)
    }
  )

  return instance
}

// 處理業務錯誤
const handleBusinessError = (data: Response) => {
  switch (data.errCode) {
    case '401':
      showErrorMessage('登錄已過期，請重新登錄')
      setTimeout(() => {
        clearAuth()
        window.location.reload()
      }, 1500)
      break
    case '403':
      showErrorMessage('權限不足')
      break
    case '429':
      showErrorMessage('操作過於頻繁，請稍後再試')
      break
    default:
      if (data.errMsg) {
        showErrorMessage(data.errMsg)
      }
  }
}

// 處理HTTP錯誤
const handleHttpError = (status: number, data: Response | { message?: string; errMsg?: string }, config?: RequestConfig) => {
  if (config?.skipErrorHandler) return

  const errorMessage = 'errMsg' in data ? data.errMsg : ('message' in data ? data.message : undefined)

  switch (status) {
    case 400:
      showErrorMessage(errorMessage || '請求參數錯誤')
      break
    case 401:
      showErrorMessage('認證失敗，請重新登錄')
      clearAuth()
      setTimeout(() => window.location.reload(), 1500)
      break
    case 403:
      showErrorMessage('權限不足')
      break
    case 404:
      showErrorMessage('請求的資源不存在')
      break
    case 500:
      showErrorMessage('服務器內部錯誤')
      break
    case 502:
      showErrorMessage('網關錯誤')
      break
    case 503:
      showErrorMessage('服務暫不可用')
      break
    default:
      showErrorMessage(`請求失敗 (${status})`)
  }
}

// 顯示錯誤消息
const showErrorMessage = (message: string) => {
  // 創建簡單的toast提示
  const toast = document.createElement('div')
  toast.textContent = message
  toast.style.cssText = `
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(255, 107, 107, 0.95);
    color: white;
    padding: 12px 24px;
    border-radius: 25px;
    z-index: 10000;
    font-weight: bold;
    box-shadow: 0 4px 15px rgba(255, 107, 107, 0.3);
  `
  document.body.appendChild(toast)
  setTimeout(() => {
    if (document.body.contains(toast)) {
      document.body.removeChild(toast)
    }
  }, config.ui.toastDuration)
}

// 聯合響應類型
type ApiResponseType<T> = SingleResponse<T> | MultiResponse<T> | PageResponse<T>

// 重試機制
const createRetryRequest = (instance: AxiosInstance) => {
  return async <T = unknown>(requestConfig: RequestConfig): Promise<AxiosResponse<ApiResponseType<T>>> => {
    const maxRetries = requestConfig.retryAttempts ?? config.api.retryAttempts
    let lastError: Error | null = null

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await instance.request<ApiResponseType<T>>(requestConfig)
        return response
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error))
        
        // 如果是最後一次嘗試，或者是不應該重試的錯誤，直接拋出
        if (attempt === maxRetries || !shouldRetry(error as AxiosError)) {
          throw lastError
        }
        
        // 等待後重試
        await new Promise(resolve => 
          setTimeout(resolve, config.api.retryDelay * (attempt + 1))
        )
        
        logger.warn(`Request retry ${attempt + 1}/${maxRetries}:`, requestConfig.url)
      }
    }
    
    throw lastError || new Error('重試失敗')
  }
}

// 判斷是否應該重試
const shouldRetry = (error: AxiosError): boolean => {
  // 網絡錯誤或超時
  if (!error.response || error.code === 'ECONNABORTED') {
    return true
  }
  
  // 服務器錯誤（5xx）
  if (error.response.status >= 500) {
    return true
  }
  
  // 429 太多請求
  if (error.response.status === 429) {
    return true
  }
  
  return false
}

// 創建實例
const axiosInstance = createAxiosInstance()
const retryRequest = createRetryRequest(axiosInstance)

// 類型判斷函數
export function isSingleResponse<T>(response: Response): response is SingleResponse<T> {
  const hasData = 'data' in response
  const dataValue = (response as SingleResponse<T>).data
  return hasData && !Array.isArray(dataValue) && !('totalCount' in response)
}

export function isMultiResponse<T>(response: Response): response is MultiResponse<T> {
  const hasData = 'data' in response
  const dataValue = (response as MultiResponse<T>).data
  return hasData && Array.isArray(dataValue) && !('totalCount' in response)
}

export function isPageResponse<T>(response: Response): response is PageResponse<T> {
  const hasData = 'data' in response
  const dataValue = (response as PageResponse<T>).data
  return hasData && Array.isArray(dataValue) && 'totalCount' in response
}

// 封裝常用請求方法
export class ApiClient {
  // 通用請求方法
  private static async request<T>(config: RequestConfig & { url: string }): Promise<ApiResponseType<T>> {
    const response = await retryRequest<T>(config)
    return response.data as ApiResponseType<T>
  }

  // GET請求 - 單條數據
  static async get<T>(url: string, config?: RequestConfig): Promise<SingleResponse<T>> {
    const response = await this.request<T>({ ...config, method: 'GET', url })
    if (isSingleResponse<T>(response)) {
      return response
    }
    throw new Error('響應格式不匹配：期望單條數據響應')
  }

  // GET請求 - 多條數據
  static async getList<T>(url: string, config?: RequestConfig): Promise<MultiResponse<T>> {
    const response = await this.request<T>({ ...config, method: 'GET', url })
    if (isMultiResponse<T>(response)) {
      return response
    }
    throw new Error('響應格式不匹配：期望多條數據響應')
  }

  // GET請求 - 分頁數據
  static async getPage<T>(url: string, config?: RequestConfig): Promise<PageResponse<T>> {
    const response = await this.request<T>({ ...config, method: 'GET', url })
    if (isPageResponse<T>(response)) {
      return response
    }
    throw new Error('響應格式不匹配：期望分頁數據響應')
  }

  // GET請求 - 自動判斷類型
  static async getAuto<T>(url: string, config?: RequestConfig): Promise<ApiResponseType<T>> {
    return await this.request<T>({ ...config, method: 'GET', url })
  }

  // POST請求 - 通常返回單條數據
  static async post<T>(url: string, data?: unknown, config?: RequestConfig): Promise<SingleResponse<T>> {
    const response = await this.request<T>({ ...config, method: 'POST', url, data })
    if (isSingleResponse<T>(response)) {
      return response
    }
    throw new Error('響應格式不匹配：期望單條數據響應')
  }

  // POST請求 - 自動判斷類型
  static async postAuto<T>(url: string, data?: unknown, config?: RequestConfig): Promise<ApiResponseType<T>> {
    return await this.request<T>({ ...config, method: 'POST', url, data })
  }

  // PUT請求
  static async put<T>(url: string, data?: unknown, config?: RequestConfig): Promise<SingleResponse<T>> {
    const response = await this.request<T>({ ...config, method: 'PUT', url, data })
    if (isSingleResponse<T>(response)) {
      return response
    }
    throw new Error('響應格式不匹配：期望單條數據響應')
  }

  // DELETE請求
  static async delete<T>(url: string, config?: RequestConfig): Promise<SingleResponse<T>> {
    const response = await this.request<T>({ ...config, method: 'DELETE', url })
    if (isSingleResponse<T>(response)) {
      return response
    }
    throw new Error('響應格式不匹配：期望單條數據響應')
  }

  // 上傳文件
  static async upload<T>(url: string, file: File, config?: RequestConfig): Promise<SingleResponse<T>> {
    const formData = new FormData()
    formData.append('file', file)
    
    const response = await this.request<T>({
      ...config,
      method: 'POST',
      url,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    
    if (isSingleResponse<T>(response)) {
      return response
    }
    throw new Error('響應格式不匹配：期望單條數據響應')
  }
}

// 導出axios實例（供特殊需求使用）
export { axiosInstance }

// 導出端點配置
export { endpoints }

export default ApiClient